// @ts-nocheck
/**
 * OCS Header Mega Panels
 *
 * Bridges Shopify-rendered mega-panel theme blocks into the static desktop
 * header menu and mobile drawer. The panel block owns its settings and exposes
 * hidden templates; this script only matches, clones, and places that markup.
 */
(function () {
  'use strict';

  const existing = window.OCSHeaderMegaPanels;
  if (existing && typeof existing.enhance === 'function') {
    existing.enhance();
    return;
  }

  const SOURCE_SELECTOR = '[data-ocs-mega-panel-source]';
  const SLOT_SELECTOR = '[data-ocs-mega-panel-slot]';
  const TARGET_SELECTOR = '[data-ocs-mega-panel-target]';
  const TEMPLATE_SELECTOR = '[data-ocs-mega-panel-template]';
  const REOCCURRING_LINK_SELECTOR = '[data-ocs-reoccurring-menu-link]';
  const REOCCURRING_LINK_ITEM_SELECTOR = '[data-ocs-reoccurring-menu-link-item]';
  const MENU_ITEM_URL_ATTR = 'data-ocs-menu-item-url';
  const DRAWER_ITEM_CLASS = 'menu-drawer__list-item';
  const DRAWER_CHILD_LIST_CLASS = 'menu-drawer__menu--childlist';
  const DRAWER_GRANDCHILD_LIST_CLASS = 'menu-drawer__menu--grandchildlist';
  const RENDERED_ATTR = 'data-ocs-mega-panel-rendered';
  const DEBUG_PREFIX = '[ocs-header-mega-panels]';
  const DEBUG_QUERY_PARAM = 'ocsMegaPanelsDebug';
  const COLUMN_COUNTS = {
    '2-column': '2',
    '3-column': '3',
    '4-column': '4',
  };
  const COLUMN_LAYOUT_CLASSES = Object.keys(COLUMN_COUNTS).map((key) => `mega-menu__list--${key}`);
  const WRAP_CLASS = 'mega-menu__list--wrap';
  const FEATURED_LAYOUTS = {
    1: {
      name: 'one',
      menuItemsWidth: '80%',
      featuredWidth: '20%',
    },
    2: {
      name: 'two',
      menuItemsWidth: '50%',
      featuredWidth: '50%',
    },
  };

  /**
   * The reoccurring CTA renders into two unrelated markup systems: the desktop
   * mega menu, styled in blocks/_header-menu.liquid, and the mobile drawer,
   * styled in snippets/header-drawer.liquid. Neither stylesheet knows about the
   * other's classes, so the element has to be built to match wherever it lands.
   *
   * `standaloneTag` differs for a structural reason, not a cosmetic one: on
   * desktop the fallback is inserted after an <a> inside a column div, on mobile
   * after an <li> inside a <ul>, where only an <li> is valid.
   */
  const REOCCURRING_VARIANTS = {
    desktop: {
      link: 'mega-menu__link mega-menu__reoccurring-link',
      underline: 'mega-menu__reoccurring-link--underlined',
      label: 'mega-menu__link-title wrap-text',
      arrow: 'mega-menu__reoccurring-link-arrow',
      item: 'mega-menu__reoccurring-link-item',
      itemTag: 'li',
      standalone: 'mega-menu__reoccurring-link-item mega-menu__reoccurring-link-item--standalone',
      standaloneTag: 'div',
    },
    mobile: {
      link: 'menu-drawer__menu-item menu-drawer__menu-item--child menu-drawer__reoccurring-link focus-inset',
      underline: 'menu-drawer__reoccurring-link--underlined',
      label: 'menu-drawer__menu-item-text wrap-text',
      arrow: 'menu-drawer__reoccurring-link-arrow',
      item: 'menu-drawer__list-item menu-drawer__list-item--reoccurring',
      itemTag: 'li',
      standalone: 'menu-drawer__list-item menu-drawer__list-item--reoccurring',
      standaloneTag: 'li',
    },
  };

  let scheduled = false;
  let observer = null;

  function normalizeText(value) {
    let normalized = String(value || '').trim().toLowerCase();

    try {
      normalized = normalized.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    } catch (_) {
      // Ignore browsers that do not support String.normalize.
    }

    return normalized.replace(/\s+/g, ' ');
  }

  function parsePositiveInteger(value) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  function getFeaturedTileCount(source) {
    const count = parsePositiveInteger(source?.dataset.featuredTileCount) || 0;
    return Math.min(count, 2);
  }

  function getFeaturedLayout(source) {
    return FEATURED_LAYOUTS[getFeaturedTileCount(source)] || null;
  }

  function isDebugEnabled() {
    if (window.OCS_HEADER_MEGA_PANELS_DEBUG === true) {
      return true;
    }

    try {
      return new URLSearchParams(window.location.search).get(DEBUG_QUERY_PARAM) === '1';
    } catch (_) {
      return false;
    }
  }

  function logDebug(label, payload) {
    if (typeof console === 'undefined' || typeof console.log !== 'function') return;

    console.log(DEBUG_PREFIX, label, payload);
  }

  function hashString(value) {
    let hash = 0;
    const text = String(value || '');

    for (let index = 0; index < text.length; index += 1) {
      hash = (hash << 5) - hash + text.charCodeAt(index);
      hash |= 0;
    }

    return String(hash >>> 0);
  }

  function getElements(selector, root) {
    return Array.from((root || document).querySelectorAll(selector)).filter(
      (element) => element instanceof HTMLElement
    );
  }

  function buildSourceIndex() {
    const sources = getElements(SOURCE_SELECTOR);
    const byTitle = new Map();
    const fallbackSources = [];

    for (const source of sources) {
      const titles = [source.dataset.menuTitle, ...(source.dataset.menuTitleAliases || '').split('|')]
        .map(normalizeText)
        .filter(Boolean);

      for (const title of titles) {
        if (!byTitle.has(title)) {
          byTitle.set(title, source);
        }
      }

      if (titles.length === 0) {
        fallbackSources.push(source);
      }
    }

    return {
      sources,
      byTitle,
      fallback: fallbackSources.length === 1 ? fallbackSources[0] : null,
    };
  }

  function findSourceForSlot(slot, index) {
    const title = normalizeText(slot.dataset.menuTitle);
    if (title && index.byTitle.has(title)) {
      return index.byTitle.get(title);
    }

    return index.fallback;
  }

  function findTemplate(source, variant) {
    for (const template of source.querySelectorAll(TEMPLATE_SELECTOR)) {
      if (template instanceof HTMLTemplateElement && template.dataset.ocsMegaPanelTemplate === variant) {
        return template;
      }
    }

    return null;
  }

  function templateHasContent(source, variant) {
    const template = findTemplate(source, variant);
    return Boolean(template && template.innerHTML.trim());
  }

  function summarizeSource(source) {
    return {
      id: source.dataset.ocsMegaPanelId || '',
      menuTitle: source.dataset.menuTitle || '',
      columnLayout: source.dataset.columnLayout || '',
      maxHeight: source.dataset.maxHeight || '',
      featuredTileCount: getFeaturedTileCount(source),
      featuredLayout: getFeaturedLayout(source)?.name || '',
      reoccurringCtaEnabled: source.dataset.ocsReoccurringCtaEnabled === 'true',
      reoccurringCtaText: source.dataset.ocsReoccurringCtaText || '',
      reoccurringCtaUnderlineText: source.dataset.ocsReoccurringCtaUnderlineText === 'true',
      desktopTemplateHasContent: templateHasContent(source, 'desktop'),
      desktopCtaTemplateHasContent: templateHasContent(source, 'desktop-cta'),
      mobileTemplateHasContent: templateHasContent(source, 'mobile'),
    };
  }

  function summarizeSlotMatch(slot, source) {
    return {
      variant: slot.dataset.ocsMegaPanelSlot || 'desktop',
      slotTitle: slot.dataset.menuTitle || '',
      matchedSource: source
        ? {
            id: source.dataset.ocsMegaPanelId || '',
            menuTitle: source.dataset.menuTitle || '',
            featuredTileCount: getFeaturedTileCount(source),
            featuredLayout: getFeaturedLayout(source)?.name || '',
            reoccurringCtaEnabled: source.dataset.ocsReoccurringCtaEnabled === 'true',
          }
        : null,
    };
  }

  function removeWhitespaceTextNodes(root) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const whitespaceNodes = [];

    while (walker.nextNode()) {
      if (!walker.currentNode.nodeValue.trim()) {
        whitespaceNodes.push(walker.currentNode);
      }
    }

    for (const node of whitespaceNodes) {
      node.remove();
    }
  }

  function getPanelElements(slot) {
    const target = slot.closest(TARGET_SELECTOR);
    const grid = target?.querySelector('[data-menu-grid-id]') || null;
    const menu = grid?.closest('.mega-menu') || slot.closest('.mega-menu') || null;
    const menuList = target?.querySelector('[data-menu-list-id]') || null;

    return { target, menu, grid, menuList };
  }

  function getNonMegaColumnLayout(slot) {
    const headerMenu = slot.closest('header-menu');
    return (headerMenu && headerMenu.dataset.nonMegaColumnLayout) || 'auto';
  }

  function getNonMegaWrap(slot) {
    const headerMenu = slot.closest('header-menu');
    return Boolean(headerMenu && headerMenu.dataset.nonMegaWrap === 'true');
  }

  function getReoccurringCtaText(source) {
    if (source.dataset.ocsReoccurringCtaEnabled !== 'true') return '';

    return String(source.dataset.ocsReoccurringCtaText || '').trim();
  }

  function getReoccurringCtaUnderlineText(source) {
    return source.dataset.ocsReoccurringCtaUnderlineText === 'true';
  }

  function isUsefulMenuHref(href) {
    const value = String(href || '').trim();
    if (!value || value === '#' || value.startsWith('#')) return false;

    const lowerValue = value.toLowerCase();
    return !lowerValue.startsWith('javascript:') && lowerValue !== 'about:blank';
  }

  function isListElement(element) {
    return element instanceof HTMLElement && element.tagName === 'UL';
  }

  function findChildMenuList(parentLink) {
    const container = parentLink.parentElement;
    if (!(container instanceof HTMLElement)) return null;

    if (isListElement(parentLink.nextElementSibling)) {
      return parentLink.nextElementSibling;
    }

    return Array.from(container.children).find(isListElement) || null;
  }

  function getReoccurringVariant(variant) {
    return REOCCURRING_VARIANTS[variant] || REOCCURRING_VARIANTS.desktop;
  }

  /**
   * data-ocs-cta-title is set from the same `text` used for the visible label,
   * not left for ocs-cta-analytics.js to read back off that label at click
   * time: this link is appended into the desktop mega-menu column, which
   * declares its own data-ocs-cta-title on every Liquid-rendered link for the
   * same reason — a declared value survives a visitor's page-translation tool
   * rewriting the rendered text, a DOM read would not.
   */
  function createReoccurringMenuLink(variant, text, href, underlineText = false) {
    const classes = getReoccurringVariant(variant);
    const link = document.createElement('a');
    link.className = underlineText ? `${classes.link} ${classes.underline}` : classes.link;
    link.dataset.ocsReoccurringMenuLink = 'true';
    link.dataset.ocsCtaTitle = text;
    link.setAttribute('href', href);

    const label = document.createElement('span');
    label.className = classes.label;
    // Existing settings commonly include a trailing >. Production renders that
    // arrow separately, so remove only the final marker and preserve all other text.
    label.textContent = String(text).replace(/\s*>\s*$/, '').trim() || text;

    const arrow = document.createElement('span');
    arrow.className = classes.arrow;
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = '>';

    link.append(label);
    link.append(arrow);

    return link;
  }

  function createReoccurringMenuLinkItem(variant, text, href, underlineText = false) {
    const classes = getReoccurringVariant(variant);
    const item = document.createElement(classes.itemTag);
    item.className = classes.item;
    item.dataset.ocsReoccurringMenuLinkItem = 'true';
    item.append(createReoccurringMenuLink(variant, text, href, underlineText));

    return item;
  }

  function createStandaloneReoccurringMenuLinkItem(variant, text, href, underlineText = false) {
    const classes = getReoccurringVariant(variant);
    const item = document.createElement(classes.standaloneTag);
    item.className = classes.standalone;
    item.dataset.ocsReoccurringMenuLinkItem = 'true';
    item.append(createReoccurringMenuLink(variant, text, href, underlineText));

    return item;
  }

  function hasClass(element, name) {
    return Boolean(element && element.classList && element.classList.contains(name));
  }

  function childElements(element) {
    return element && element.children ? Array.from(element.children) : [];
  }

  /**
   * Level-2 links in the desktop mega menu, each paired with the level-3 list
   * the reoccurring CTA belongs at the bottom of.
   *
   * @param {HTMLElement} root the [data-menu-list-id] list
   * @returns {Array<{href: string, childList: HTMLElement|null, anchor: HTMLElement}>}
   */
  function collectDesktopReoccurringTargets(root) {
    const targets = [];

    for (const parentLink of getElements('.mega-menu__column > div > .mega-menu__link', root)) {
      const href = String(parentLink.getAttribute('href') || '').trim();
      if (!isUsefulMenuHref(href)) continue;

      targets.push({ href, childList: findChildMenuList(parentLink), anchor: parentLink });
    }

    return targets;
  }

  /**
   * `item > accordion-custom > details > ul.menu-drawer__menu--grandchildlist`,
   * walked by child rather than by selector so the whole collector stays
   * exercisable against a plain object tree in tests.
   *
   * @param {HTMLElement} item
   * @returns {HTMLElement|null}
   */
  function findDrawerGrandchildList(item) {
    for (const accordion of childElements(item)) {
      if (String(accordion.tagName || '').toUpperCase() !== 'ACCORDION-CUSTOM') continue;

      for (const details of childElements(accordion)) {
        for (const candidate of childElements(details)) {
          if (hasClass(candidate, DRAWER_GRANDCHILD_LIST_CLASS)) return candidate;
        }
      }
    }

    return null;
  }

  /**
   * The mobile equivalent, over the drawer's own markup.
   *
   * The href cannot come from the DOM here — a level-2 item with children is a
   * <summary>, not a link — so it is read from the data attribute
   * snippets/header-drawer.liquid writes onto the shared <li>. Items this script
   * injected carry no such attribute, so they are skipped rather than compounded.
   *
   * @param {HTMLElement} root the [data-ocs-mega-panel-target] list item
   * @returns {Array<{href: string, childList: HTMLElement|null, anchor: HTMLElement}>}
   */
  function collectMobileReoccurringTargets(root) {
    const targets = [];

    if (!root || typeof root.querySelector !== 'function') return targets;

    const childList = root.querySelector(`.${DRAWER_CHILD_LIST_CLASS}`);
    if (!childList) return targets;

    for (const item of childElements(childList)) {
      if (!hasClass(item, DRAWER_ITEM_CLASS)) continue;

      const href = String(item.getAttribute(MENU_ITEM_URL_ATTR) || '').trim();
      if (!isUsefulMenuHref(href)) continue;

      targets.push({ href, childList: findDrawerGrandchildList(item), anchor: item });
    }

    return targets;
  }

  function collectReoccurringTargets(root, variant) {
    return variant === 'mobile' ? collectMobileReoccurringTargets(root) : collectDesktopReoccurringTargets(root);
  }

  function clearReoccurringMenuLinks(slot) {
    const { target, menuList } = getPanelElements(slot);
    const root = menuList || target;
    if (!(root instanceof HTMLElement)) return;

    for (const item of root.querySelectorAll(REOCCURRING_LINK_ITEM_SELECTOR)) {
      item.remove();
    }

    for (const link of root.querySelectorAll(REOCCURRING_LINK_SELECTOR)) {
      link.remove();
    }
  }

  function syncReoccurringMenuLinks(source, slot, variant) {
    if (!managesReoccurringLinks(variant)) return;

    clearReoccurringMenuLinks(slot);

    const ctaText = getReoccurringCtaText(source);
    if (!ctaText) return;
    const underlineText = getReoccurringCtaUnderlineText(source);

    // Desktop hangs its links off the mega menu's own list; the drawer has no
    // equivalent, so the panel target is the closest enclosing scope there.
    const { target, menuList } = getPanelElements(slot);
    const root = variant === 'mobile' ? target : menuList;
    if (!(root instanceof HTMLElement)) return;

    for (const entry of collectReoccurringTargets(root, variant)) {
      if (entry.childList) {
        entry.childList.append(createReoccurringMenuLinkItem(variant, ctaText, entry.href, underlineText));
        continue;
      }

      entry.anchor.insertAdjacentElement(
        'afterend',
        createStandaloneReoccurringMenuLinkItem(variant, ctaText, entry.href, underlineText)
      );
    }
  }

  function resetColumnLayout(element) {
    if (!(element instanceof HTMLElement)) return;

    delete element.dataset.columnLayout;
    element.classList.remove(...COLUMN_LAYOUT_CLASSES);
  }

  function applyColumnLayout(element, columnLayout) {
    if (!(element instanceof HTMLElement)) return;

    resetColumnLayout(element);

    if (!columnLayout || columnLayout === 'auto') return;
    if (!COLUMN_COUNTS[columnLayout]) return;

    element.dataset.columnLayout = columnLayout;
    element.classList.add(`mega-menu__list--${columnLayout}`);
  }

  function resetMenuWrap(element) {
    if (!(element instanceof HTMLElement)) return;
    element.classList.remove(WRAP_CLASS);
  }

  function applyMenuWrap(element, wrap) {
    if (!(element instanceof HTMLElement)) return;
    resetMenuWrap(element);
    if (!wrap) return;
    element.classList.add(WRAP_CLASS);
  }

  function resetMaxHeight(element) {
    if (!(element instanceof HTMLElement)) return;

    element.style.removeProperty('--submenu-max-height');
    element.style.removeProperty('max-height');
  }

  function resetFeaturedLayout(element) {
    if (!(element instanceof HTMLElement)) return;

    delete element.dataset.featuredLayout;
    delete element.dataset.featuredTileCount;
    element.style.removeProperty('--ocs-mega-panel-menu-width');
    element.style.removeProperty('--ocs-mega-panel-featured-width');
  }

  function applyFeaturedLayout(source, slot, variant) {
    const { target, grid, menuList } = getPanelElements(slot);

    for (const element of [target, grid, menuList, slot]) {
      resetFeaturedLayout(element);
    }

    if (variant !== 'desktop') return;

    const featuredTileCount = getFeaturedTileCount(source);
    const layout = getFeaturedLayout(source);
    if (!layout) return;

    for (const element of [target, grid, menuList, slot]) {
      if (!(element instanceof HTMLElement)) continue;

      element.dataset.featuredTileCount = String(featuredTileCount);
      element.dataset.featuredLayout = layout.name;
      element.style.setProperty('--ocs-mega-panel-menu-width', layout.menuItemsWidth);
      element.style.setProperty('--ocs-mega-panel-featured-width', layout.featuredWidth);
    }
  }

  function clearPanelSettings(slot) {
    const { target, menu, grid, menuList } = getPanelElements(slot);
    const nonMegaColumnLayout = getNonMegaColumnLayout(slot);
    const nonMegaWrap = getNonMegaWrap(slot);

    for (const element of [target, grid, menuList, slot]) {
      applyColumnLayout(element, nonMegaColumnLayout);
      resetFeaturedLayout(element);
      applyMenuWrap(element, nonMegaWrap);
    }

    for (const element of [target, menu, grid, menuList, slot]) {
      resetMaxHeight(element);
    }
  }

  function applyPanelSettings(source, slot, variant) {
    const { target, menu, grid, menuList } = getPanelElements(slot);
    const columnLayout = source.dataset.columnLayout || 'auto';
    const wrapMenuItems = source.dataset.wrapMenuItems === 'true';

    for (const element of [target, grid, menuList, slot]) {
      applyColumnLayout(element, columnLayout);
      applyMenuWrap(element, wrapMenuItems);
    }

    applyFeaturedLayout(source, slot, variant);

    for (const element of [target, menu, grid, menuList, slot]) {
      resetMaxHeight(element);
    }

    if (variant !== 'desktop') return;

    const maxHeight = parsePositiveInteger(source.dataset.maxHeight);
    if (!maxHeight || !(menu instanceof HTMLElement)) return;

    const maxHeightValue = `${maxHeight}px`;
    menu.style.setProperty('--submenu-max-height', maxHeightValue);
    menu.style.maxHeight = maxHeightValue;
  }

  function shouldManagePanelSettings(variant) {
    return variant === 'desktop' || variant === 'mobile';
  }

  function managesReoccurringLinks(variant) {
    return variant === 'desktop' || variant === 'mobile';
  }

  function clearSlot(slot) {
    if (slot.childNodes.length > 0) {
      slot.replaceChildren();
    }

    slot.removeAttribute(RENDERED_ATTR);

    const variant = slot.dataset.ocsMegaPanelSlot || 'desktop';
    if (managesReoccurringLinks(variant)) {
      clearReoccurringMenuLinks(slot);
    }

    if (shouldManagePanelSettings(variant)) {
      clearPanelSettings(slot);
    }
  }

  function renderSlot(slot, source) {
    const variant = slot.dataset.ocsMegaPanelSlot || 'desktop';
    const template = findTemplate(source, variant);

    if (!template) {
      clearSlot(slot);
      return;
    }

    const sourceSignature = [
      source.dataset.menuTitle || '',
      source.dataset.menuTitleAliases || '',
      source.dataset.columnLayout || '',
      source.dataset.maxHeight || '',
      source.dataset.featuredTileCount || '',
      source.dataset.ocsReoccurringCtaEnabled || '',
      source.dataset.ocsReoccurringCtaText || '',
      template.innerHTML,
    ].join('|');
    const renderKey = [source.dataset.ocsMegaPanelId || '', variant, hashString(sourceSignature)].join('|');

    if (slot.getAttribute(RENDERED_ATTR) !== renderKey) {
      const fragment = template.content.cloneNode(true);
      removeWhitespaceTextNodes(fragment);
      slot.replaceChildren(fragment);
      slot.setAttribute(RENDERED_ATTR, renderKey);
    }

    if (shouldManagePanelSettings(variant)) {
      applyPanelSettings(source, slot, variant);
    }

    syncReoccurringMenuLinks(source, slot, variant);
  }

  function enhance() {
    scheduled = false;

    const index = buildSourceIndex();
    const debugPayload = isDebugEnabled()
      ? {
          sources: index.sources.map(summarizeSource),
          matches: [],
        }
      : null;

    for (const slot of getElements(SLOT_SELECTOR)) {
      const source = findSourceForSlot(slot, index);

      if (debugPayload) {
        debugPayload.matches.push(summarizeSlotMatch(slot, source));
      }

      if (!source) {
        clearSlot(slot);
        continue;
      }

      renderSlot(slot, source);
    }

    if (debugPayload) {
      logDebug('enhance', debugPayload);
    }
  }

  function scheduleEnhance() {
    if (scheduled) return;

    scheduled = true;
    window.requestAnimationFrame(enhance);
  }

  function elementContainsPanelMarkup(element) {
    if (!(element instanceof Element)) return false;

    return (
      element.matches(SOURCE_SELECTOR) ||
      element.matches(SLOT_SELECTOR) ||
      element.matches(TARGET_SELECTOR) ||
      element.matches('header-component') ||
      Boolean(element.querySelector(`${SOURCE_SELECTOR}, ${SLOT_SELECTOR}, ${TARGET_SELECTOR}, header-component`))
    );
  }

  function shouldRefreshFromMutations(mutations) {
    for (const mutation of mutations) {
      if (mutation.target instanceof Element && mutation.target.closest(SLOT_SELECTOR)) {
        return true;
      }

      for (const node of mutation.addedNodes) {
        if (elementContainsPanelMarkup(node)) return true;
      }

      for (const node of mutation.removedNodes) {
        if (elementContainsPanelMarkup(node)) return true;
      }
    }

    return false;
  }

  function startObserver() {
    if (observer || !document.body) return;

    observer = new MutationObserver((mutations) => {
      if (shouldRefreshFromMutations(mutations)) {
        scheduleEnhance();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  window.OCSHeaderMegaPanels = {
    enhance: scheduleEnhance,
  };

  document.addEventListener('shopify:section:load', scheduleEnhance);
  document.addEventListener('shopify:block:select', scheduleEnhance);
  document.addEventListener('shopify:block:deselect', scheduleEnhance);

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        startObserver();
        scheduleEnhance();
      },
      { once: true }
    );
  } else {
    startObserver();
  }

  scheduleEnhance();
})();
